<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import EntrButton from './EntrButton.vue';
import EntrIcon from './EntrIcon.vue';

defineProps<{
  title: string;
  body: string;
  retryLabel?: string;
  exitLabel?: string;
  autoDismissMs?: number;
}>();

defineEmits<{
  close: [];
  retry: [];
  exit: [];
}>();

const { t } = useI18n();
</script>

<template>
  <div class="sheet-backdrop">
    <section class="sheet" role="dialog" aria-modal="true" :aria-label="title" data-testid="entr-sheet">
      <button class="close" type="button" :aria-label="t('common.close')" @click="$emit('close')">
        <EntrIcon name="x" />
      </button>
      <h2 class="t-headline" data-testid="entr-sheet-title">{{ title }}</h2>
      <p class="t-body-lg" data-testid="entr-sheet-body">{{ body }}</p>
      <EntrButton v-if="retryLabel" class="retry" data-testid="entr-sheet-retry" @click="$emit('retry')">
        {{ retryLabel }}
        <template #icon><EntrIcon name="arrow-right" /></template>
      </EntrButton>
      <EntrButton v-if="exitLabel" class="exit" data-testid="entr-sheet-exit" @click="$emit('exit')">
        {{ exitLabel }}
      </EntrButton>
      <div
        v-if="autoDismissMs"
        class="auto-dismiss-progress"
        data-testid="auto-dismiss-progress"
        :style="{ animationDuration: `${autoDismissMs}ms` }"
      />
    </section>
  </div>
</template>

<style scoped>
.sheet-backdrop {
  position: absolute;
  inset: 0;
  z-index: 20;
  pointer-events: none;
}

.sheet {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  min-height: 920px;
  padding: 96px;
  border-top-left-radius: var(--entr-radius-sheet);
  border-top-right-radius: var(--entr-radius-sheet);
  background: var(--entr-ink);
  color: var(--entr-text);
  pointer-events: auto;
}

.close {
  position: absolute;
  top: 28px;
  right: 36px;
  width: 96px;
  height: 96px;
  display: grid;
  place-items: center;
}

.close :deep(svg) {
  width: 48px;
  height: 48px;
}

p {
  max-width: 800px;
  margin-top: 32px;
}

.retry,
.exit {
  margin-top: 120px;
}

.auto-dismiss-progress {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 6px;
  background: var(--entr-logo-green);
  transform-origin: left center;
  animation-name: auto-dismiss-progress;
  animation-timing-function: linear;
  animation-fill-mode: forwards;
}

@keyframes auto-dismiss-progress {
  from {
    transform: scaleX(1);
  }

  to {
    transform: scaleX(0);
  }
}
</style>
